import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { Loader2, TrendingUp, PoundSterling } from "lucide-react";

export default function PublisherEarnings() {
  const [data, setData] = useState(null);
  const [overview, setOverview] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let alive = true;
    Promise.all([api.get(`/publisher/earnings-trend?days=${days}`), api.get("/publisher/overview")])
      .then(([t, o]) => { if (alive) { setData(t.data); setOverview(o.data); } })
      .catch(() => alive && setData({ series: [] }));
    return () => { alive = false; };
  }, [days]);

  // Build SVG bar chart series
  const chart = useMemo(() => {
    const series = data?.series || [];
    if (series.length === 0) return null;
    const max = Math.max(...series.map((p) => p.earnings_gbp), 1);
    const W = 100, H = 28;
    return { series, max, W, H };
  }, [data]);

  return (
    <div className="space-y-6" data-testid="publisher-earnings-page">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Earnings</h1>
          <p className="text-[13px] text-slate-500 mt-1">Daily earnings trend with running totals.</p>
        </div>
        <div className="inline-flex items-center bg-slate-100 rounded-full p-0.5 text-[11px]">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded-full transition-colors ${days === d ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>{d}d</button>
          ))}
        </div>
      </div>

      {/* KPI strip from overview */}
      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <K label="Earnings (lifetime)" value={`£${(overview.kpis?.earnings_total_gbp || 0).toFixed(2)}`} icon={PoundSterling} />
          <K label="Available balance"   value={`£${(overview.kpis?.available_balance_gbp || 0).toFixed(2)}`} icon={PoundSterling} highlight />
          <K label="Paid out"            value={`£${(overview.kpis?.paid_out_gbp || 0).toFixed(2)}`} icon={TrendingUp} />
          <K label="Revenue share"       value={`${overview.limits?.revenue_share_pct ?? 90}%`} icon={TrendingUp} sub="Per accepted lead" />
        </div>
      )}

      {!data && <div className="text-center py-12 text-slate-500"><Loader2 className="size-4 animate-spin inline mr-1" />Loading…</div>}

      {data && data.series.length === 0 && (
        <Card className="rounded-xl border-slate-200">
          <CardContent className="p-12 text-center text-slate-500 text-[13px]">No earnings activity in this window.</CardContent>
        </Card>
      )}

      {chart && (
        <Card className="rounded-xl border-slate-200">
          <CardContent className="p-6">
            <div className="text-[13px] font-medium text-slate-900 mb-1">Daily earnings · last {days} days</div>
            <div className="text-[11px] text-slate-500 mb-4">Total in window: <span className="font-mono tabular-nums text-slate-900">£{chart.series.reduce((s, p) => s + p.earnings_gbp, 0).toFixed(2)}</span></div>
            <div className="flex items-end gap-1 h-44">
              {chart.series.map((p, i) => {
                const h = (p.earnings_gbp / chart.max) * 100;
                return (
                  <div key={p.date} className="flex-1 flex flex-col items-center group" data-testid={`earnings-bar-${i}`}>
                    <div className="relative w-full flex items-end h-full">
                      <div className="w-full bg-gradient-to-t from-[#0055FF] to-[#22D3A6] rounded-t-sm transition-all hover:opacity-90"
                           style={{ height: `${Math.max(h, p.earnings_gbp > 0 ? 4 : 0)}%` }} />
                      <div className="absolute -top-7 left-1/2 -translate-x-1/2 hidden group-hover:block whitespace-nowrap bg-slate-900 text-white text-[10px] px-2 py-0.5 rounded">
                        £{p.earnings_gbp.toFixed(2)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 grid grid-cols-2 lg:grid-cols-6 gap-2 text-[10px] text-slate-400 font-mono">
              {chart.series.map((p, i) => (
                <span key={p.date} className={i % Math.max(1, Math.floor(chart.series.length / 6)) ? "opacity-0" : ""}>
                  {p.date.slice(5)}
                </span>
              )).slice(0, 6)}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table of daily entries */}
      {data && data.series.length > 0 && (
        <Card className="rounded-xl border-slate-200">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/60 border-b border-slate-200 text-left text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-5 py-3 font-medium">Date</th>
                    <th className="font-medium text-right">Leads accepted</th>
                    <th className="font-medium text-right pr-5">Earnings</th>
                  </tr>
                </thead>
                <tbody>
                  {data.series.slice().reverse().map((p) => (
                    <tr key={p.date} className="border-t border-slate-100 hover:bg-slate-50/40">
                      <td className="px-5 py-3 text-[13px] text-slate-700">{new Date(p.date).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" })}</td>
                      <td className="py-3 text-right tabular-nums text-slate-700">{p.leads}</td>
                      <td className="py-3 pr-5 text-right tabular-nums text-slate-900 font-medium">£{p.earnings_gbp.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function K({ label, value, icon: Icon, highlight, sub }) {
  return (
    <Card className={`rounded-xl border-slate-200 ${highlight ? "bg-[#0055FF]/[0.025]" : ""}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500">
          <Icon className="size-3" />{label}
        </div>
        <div className={`mt-1 text-2xl font-semibold tabular-nums ${highlight ? "text-[#0055FF]" : "text-slate-900"}`}>{value}</div>
        {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
}
